import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Eye, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, DataTable, Field, Select, Badge } from "@/components/common";

export const PERM_LABEL = (k) => k.split(":")[1].replace(/_/g, " ");

export function PermGrid({ perms, onToggle, testid }) {
  const { meta } = useApp();
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid={testid}>
      {[["Pages", (meta?.pages || []).map((p) => `page:${p}`)], ["Actions", (meta?.actions || []).map((a) => `action:${a}`)]].map(([title, keys]) => (
        <div key={title}>
          <div className="lbl mb-2">{title}</div>
          <div className="grid grid-cols-2 gap-1.5">
            {keys.map((k) => (
              <label key={k} className="flex items-center gap-2 rounded border px-2 py-1.5 text-xs capitalize">
                <input type="checkbox" checked={!!perms[k]} onChange={(e) => onToggle(k, e.target.checked)} data-testid={`perm-${k.replace(":", "-")}`} />{PERM_LABEL(k)}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function UserDialog({ open, onClose, u, onSaved }) {
  const { meta } = useApp();
  const [f, setF] = useState({});
  const [sups, setSups] = useState([]);
  useEffect(() => {
    if (open) {
      setF(u ? { ...u, password: "" } : { role: "employee", email_alerts: false });
      api.get("/users").then((r) => setSups(r.data.filter((x) => x.role === "supervisor")));
    }
  }, [open, u]);
  const save = async () => {
    try {
      if (u) await api.put(`/users/${u.id}`, f);
      else await api.post("/users", f);
      toast.success("Employee saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{u ? "Edit employee" : "Create employee"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Full name" required><input className="inp" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></Field>
          <Field label="Email" required><input className="inp" disabled={!!u} value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></Field>
          <Field label="Role" required><Select value={f.role} onChange={(v) => setF({ ...f, role: v })} testid="user-role-select" options={[{ value: "employee", label: "Wellness Navigator (Employee)" }, { value: "supervisor", label: "Supervisor" }, { value: "front_desk", label: "Front Desk" }, { value: "admin", label: "System Administrator" }]} /></Field>
          <Field label="Supervisor"><Select value={f.supervisor_id} onChange={(v) => setF({ ...f, supervisor_id: v || null })} placeholder="None" testid="user-supervisor-select" options={sups.map((s) => ({ value: s.id, label: s.name }))} /></Field>
          <Field label="Location"><Select value={f.location_id} onChange={(v) => setF({ ...f, location_id: v || null })} placeholder="None" testid="user-location-select" options={(meta?.locations || []).map((l) => ({ value: l.id, label: l.name }))} /></Field>
          {(meta?.locations || []).length > 1 && (
            <div className="sm:col-span-2"><span className="lbl">Additional locations</span>
              <div className="mt-1.5 flex flex-wrap gap-2" data-testid="user-extra-locations">
                {(meta?.locations || []).filter((l) => l.id !== f.location_id).map((l) => (
                  <label key={l.id} className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs">
                    <input type="checkbox" checked={(f.location_ids || []).includes(l.id)} data-testid={`user-extra-loc-${l.id}`}
                      onChange={(e) => setF({ ...f, location_ids: e.target.checked ? [...(f.location_ids || []), l.id] : (f.location_ids || []).filter((x) => x !== l.id) })} />{l.name}
                  </label>
                ))}
              </div></div>
          )}
          <Field label={u ? "Reset password (optional)" : "Temporary password"} required={!u}><input className="inp" type="password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" /></Field>
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={!!f.email_alerts} onChange={(e) => setF({ ...f, email_alerts: e.target.checked })} data-testid="user-email-alerts" />Send email alerts (no patient details) for missed appointments and escalations</label>
        </div>
        <div className="flex justify-end gap-2"><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} data-testid="user-save-btn">Save</button></div>
      </DialogContent>
    </Dialog>
  );
}

function PermDialog({ open, onClose, u, onSaved }) {
  const [perms, setPerms] = useState({});
  const [over, setOver] = useState({});
  const [grants, setGrants] = useState("");
  useEffect(() => {
    if (open && u) {
      api.get(`/users/${u.id}/effective`).then((r) => setPerms(r.data.perms));
      setOver(u.permission_overrides || {});
      setGrants((u.granted_patient_ids || []).join(", "));
    }
  }, [open, u]);
  const save = async (reset) => {
    try {
      let ids = [];
      if (grants.trim()) {
        const pts = (await api.get("/patients")).data;
        const codes = grants.split(",").map((s) => s.trim()).filter(Boolean);
        ids = pts.filter((p) => codes.includes(p.patient_id)).map((p) => p.id);
      }
      await api.put(`/users/${u.id}`, { permission_overrides: reset ? {} : over, granted_patient_ids: ids });
      toast.success("Permissions updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="perm-dialog">
        <DialogHeader><DialogTitle>Permissions — {u?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-[var(--muted-ink)]">Checked items are effective permissions (role defaults plus individual overrides). Changes here are saved as overrides for this user and enforced by the server.</p>
        <PermGrid testid="user-perm-grid" perms={perms} onToggle={(k, v) => { setPerms({ ...perms, [k]: v }); setOver({ ...over, [k]: v }); }} />
        <Field label="Explicit patient access grants (Patient IDs, comma separated)"><input className="inp" value={grants} onChange={(e) => setGrants(e.target.value)} data-testid="perm-grants-input" placeholder="e.g. SYN-1005, SYN-1010" /></Field>
        <div className="flex justify-between gap-2">
          <button className="btn btn-ghost" onClick={() => save(true)} data-testid="perm-reset-btn">Reset to role defaults</button>
          <button className="btn btn-primary" onClick={() => save(false)} data-testid="perm-save-btn">Save permissions</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Employees() {
  const { can, startPreview, user } = useApp();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [perm, setPerm] = useState(null);
  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const manage = can("action:manage_users");
  const names = Object.fromEntries(rows.map((r) => [r.id, r.name]));
  const toggle = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Employee deactivated (history preserved)" : "Employee reactivated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [{ key: "name", label: "Employee Name", render: (r) => <span className="font-semibold">{r.name}</span> }, { key: "email", label: "Email" },
    { key: "role", label: "Role", render: (r) => <span className="capitalize">{r.role.replace("_", " ")}</span> },
    { key: "supervisor", label: "Supervisor", text: (r) => names[r.supervisor_id] || "", render: (r) => names[r.supervisor_id] || "—" },
    { key: "patient_count", label: "Assigned Patients" }, { key: "mfa_enabled", label: "MFA", render: (r) => (r.mfa_enabled ? "On" : "Off") },
    { key: "active", label: "Status", render: (r) => <Badge value={r.active !== false ? "ACTIVE" : "INACTIVE"} /> }];
  if (manage) cols.push({ key: "_a", label: "Actions", sortable: false, render: (r) => (
    <div className="flex gap-1.5">
      <button className="btn btn-ghost btn-sm" onClick={() => setDlg({ u: r })} data-testid={`edit-user-${r.email}`}>Edit</button>
      {r.role !== "admin" && <button className="btn btn-ghost btn-sm" onClick={() => setPerm(r)} data-testid={`perms-user-${r.email}`}><KeyRound size={12} />Permissions</button>}
      {r.role !== "admin" && user.role === "admin" && <button className="btn btn-ghost btn-sm" onClick={() => { startPreview(r); nav("/"); }} data-testid={`preview-user-${r.email}`}><Eye size={12} />Preview</button>}
      {r.id !== user.id && <button className="btn btn-ghost btn-sm" onClick={() => toggle(r)} data-testid={`toggle-user-${r.email}`}>{r.active !== false ? "Deactivate" : "Activate"}</button>}
    </div>) });
  return (
    <div>
      <PageHeader title="Employees" subtitle={manage ? "Create employees, assign roles, supervisors and locations, and manage individual permissions." : "Employees on your team"}
        actions={manage && <button className="btn btn-primary" onClick={() => setDlg({})} data-testid="add-user-btn"><UserPlus size={15} />Create employee</button>} />
      <DataTable testid="users-table" columns={cols} rows={rows} />
      <UserDialog open={!!dlg} u={dlg?.u} onClose={() => setDlg(null)} onSaved={load} />
      <PermDialog open={!!perm} u={perm} onClose={() => setPerm(null)} onSaved={load} />
    </div>
  );
}
